import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useHistory} from 'react-router-dom'
import {useIntl} from 'react-intl'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import {selectUser} from '../../../../app/modules/auth'
import {PlanType} from '../../../../app/pages/pricing/pricing.constant'
import {ServiceStatus} from '../../../../app/modules/services/constants'
import {
  getAll as getAllServices,
  selectAllService,
} from '../../../../app/modules/services/redux/serviceRedux'
import {HARDCODE_USER_ADMIN_FOR_TEST, USER_ROLE} from '../../../../app/shared/constants'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {MenuItem} from './MenuItem'
import './MenuInner.scss'

const truncateDescription = (text: string, wordLimit = 15) => {
  if (!text) {
    return ''
  }
  const words = text.trim().split(/\s+/)
  if (words.length <= wordLimit) {
    return text
  }
  return `${words.slice(0, wordLimit).join(' ')}...`
}

export function MenuInner() {
  const intl = useIntl()
  const history = useHistory()
  const dispatch = useDispatch()
  const user = useAppSelector(selectUser)
  const services = useAppSelector(selectAllService)
  const activeServices = useMemo(
    () => services.filter((service) => service.status === ServiceStatus.ENABLE),
    [services]
  )
  const [isServiceSelectorOpen, setIsServiceSelectorOpen] = useState(false)

  useEffect(() => {
    if (!services.length) {
      dispatch(getAllServices())
    }
  }, [dispatch, services.length])

  const navigateToNewOrder = useCallback(
    (serviceId: number) => {
      history.push(`/orders/create?serviceId=${serviceId}`)
    },
    [history]
  )

  const handleServiceSelection = useCallback(
    (serviceId: number) => {
      setIsServiceSelectorOpen(false)
      navigateToNewOrder(serviceId)
    },
    [navigateToNewOrder]
  )

  const handleNewOrderClick = useCallback(() => {
    if (!activeServices.length) {
      return
    }
    if (activeServices.length === 1) {
      navigateToNewOrder(activeServices[0].id)
      return
    }
    setIsServiceSelectorOpen(true)
  }, [activeServices, navigateToNewOrder])

  return (
    <>
      <MenuItem title={intl.formatMessage({id: 'MENU.HOME'})} to='/services' />
      <div
        className='menu-item menu-down-accordion menu-dropdown'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-start'
      >
        <span className='menu-link py-3'>
          <span className='menu-title'>{intl.formatMessage({id: 'MENU.ORDERS'})}</span>
        </span>
        <div className='menu-sub menu-sub-accordion menu-sub-lg-dropdown menu-active-bg py-4 w-100 w-lg-225px'>
          <MenuItem to='/orders' title={intl.formatMessage({id: 'MENU.ORDERS-LIST'})} />
          <MenuItem
            to='/orders/create'
            title={intl.formatMessage({id: 'MENU.ORDERS.NEW'})}
            onClick={handleNewOrderClick}
          />
        </div>
      </div>
      {user?.email === HARDCODE_USER_ADMIN_FOR_TEST && user?.role === USER_ROLE.NORMAL_USER && (
        <div
          className='menu-item menu-down-accordion menu-dropdown'
          data-kt-menu-trigger='click'
          data-kt-menu-placement='bottom-start'
        >
          <span className='menu-link py-3'>
            <span className='menu-title'>{intl.formatMessage({id: 'MENU.SCHEDULER'})}</span>
          </span>
          <div className='menu-sub menu-sub-accordion menu-sub-lg-dropdown menu-active-bg py-4 w-100 w-lg-225px'>
            <MenuItem to='/schedulers' title={intl.formatMessage({id: 'MENU.SCHEDULER-LIST'})} />
            <MenuItem
              to='/schedulers/logs'
              title={intl.formatMessage({id: 'MENU.SCHEDULER-LOGS'})}
            />
          </div>
        </div>
      )}
      {user?.planType !== PlanType.PAY_AS_U_GO &&
        user &&
        [USER_ROLE.NORMAL_USER, USER_ROLE.BRANCH_USER].includes(user.role) && (
          <div
            className='menu-item menu-down-accordion menu-dropdown'
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-start'
          >
            <span className='menu-link py-3'>
              <span className='menu-title'>{intl.formatMessage({id: 'MENU.BRANCH'})}</span>
            </span>
            <div className='menu-sub menu-sub-accordion menu-sub-lg-dropdown menu-active-bg py-4 w-100 w-lg-225px'>
              {user.role === USER_ROLE.NORMAL_USER && (
                <MenuItem to='/branch' title={intl.formatMessage({id: 'MENU.BRANCH'})} />
              )}
              <MenuItem to='/branch/users' title={intl.formatMessage({id: 'MENU.BRANCH-USER'})} />
            </div>
          </div>
        )}
      {(user?.role === USER_ROLE.NORMAL_USER ||
        (user?.role === USER_ROLE.BRANCH_USER && user.isSeparateBilling)) && (
        <MenuItem title={intl.formatMessage({id: 'MENU.PRICING'})} to='/pricing' />
      )}
      {/* <MenuItem title={intl.formatMessage({id: 'MENU.INQUIRY'})} to='/inquiries' />*/}
      <MenuItem title={intl.formatMessage({id: 'MENU.BILLING'})} to='/billings' />
      <Modal
        show={isServiceSelectorOpen}
        onHide={() => setIsServiceSelectorOpen(false)}
        centered
        size='lg'
        backdrop='static'
      >
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'MENU.ORDERS.SELECT_SERVICE'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted mb-4'>
            {intl.formatMessage({id: 'MENU.ORDERS.SELECT_SERVICE_DESC'})}
          </p>
          <div className='row g-4'>
            {activeServices.map((service) => {
              const title = intl.formatMessage({
                id: `SERVICE.SERVICE.${service.name}`,
                defaultMessage: service.name,
              })
              const description = intl.formatMessage({
                id: `SERVICE.SERVICE.${service.name}_description`,
                defaultMessage: service.description || service.subDescription || '',
              })
              const truncatedDescription = truncateDescription(description)
              const previewImage = service.imgAfter || service.imgBefore
              return (
                <div className='col-12 col-md-6' key={service.id}>
                  <button
                    type='button'
                    className='service-selection-card w-100 text-start'
                    onClick={() => handleServiceSelection(service.id)}
                    aria-label={title}
                  >
                    {previewImage && (
                      <div className='service-selection-card__image-wrapper'>
                        <img
                          src={previewImage}
                          alt={title}
                          className='service-selection-card__image'
                          loading='lazy'
                        />
                      </div>
                    )}
                    <div className='service-selection-card__body'>
                      <h5 className='service-selection-card__title mb-2'>{title}</h5>
                      {truncatedDescription && (
                        <p className='service-selection-card__description mb-0'>
                          {truncatedDescription}
                        </p>
                      )}
                    </div>
                  </button>
                </div>
              )
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={() => setIsServiceSelectorOpen(false)}>
            {intl.formatMessage({id: 'SYSTEM.MODAL.CONFIRM.CANCEL'})}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
